import { animate, stagger } from 'animejs';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Respeita "reduzir movimento" do sistema: sem animação, só o estado final. */
export const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Entrada em cascata dos filhos marcados com [data-motion-item] (fade + leve subida).
 * Roda quando `key` muda — passe algo estável (ex.: ids da lista) para não reanimar a cada refetch.
 */
export function useStaggerIn(ref: RefObject<HTMLElement | null>, key: string, enabled = true) {
    useLayoutEffect(() => {
        const root = ref.current;
        if (!root || !enabled || prefersReducedMotion()) return;
        const targets = root.querySelectorAll<HTMLElement>('[data-motion-item]');
        if (targets.length === 0) return;

        // Antes da pintura: sem isso o item aparece pronto por 1 frame e depois "pisca".
        targets.forEach((el) => {
            el.style.opacity = '0';
        });

        // Sem estilo inline sobrando: o item volta a depender só das classes.
        const clear = () =>
            targets.forEach((el) => {
                el.style.opacity = '';
                el.style.transform = '';
            });

        const anim = animate(targets, {
            opacity: [0, 1],
            translateY: [10, 0],
            duration: 420,
            delay: stagger(28, { start: 40 }),
            ease: 'outQuad',
            onComplete: clear,
        });

        return () => {
            anim.cancel();
            clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled]);
}

/** Número que "conta" até o valor novo (totais da PO, m³…). Retorna o valor a exibir. */
export function useCountUp(value: number, duration = 650): number {
    const [display, setDisplay] = useState(value);
    const current = useRef({ value });

    useEffect(() => {
        if (prefersReducedMotion() || current.current.value === value) {
            current.current.value = value;
            setDisplay(value);
            return;
        }

        const anim = animate(current.current, {
            value,
            duration,
            ease: 'outExpo',
            onUpdate: () => setDisplay(current.current.value),
        });

        return () => {
            anim.pause();
        };
    }, [value, duration]);

    return display;
}

/** Destaque rápido num elemento que acabou de mudar (item adicionado, total atualizado). */
export function flash(el: HTMLElement | null, rgb = '16, 185, 129') {
    if (!el || prefersReducedMotion()) return;
    animate(el, {
        backgroundColor: [`rgba(${rgb}, 0.22)`, `rgba(${rgb}, 0)`],
        duration: 1100,
        ease: 'outQuad',
        onComplete: () => {
            el.style.backgroundColor = '';
        },
    });
}

/** "Pop" de escala — usado em selos/contadores que mudam de estado. */
export function pop(el: HTMLElement | null) {
    if (!el || prefersReducedMotion()) return;
    animate(el, { scale: [1, 1.12, 1], duration: 420, ease: 'outBack' });
}

/** Barra que enche de 0 até `percent` (largura em %). */
export function useFillBar(ref: RefObject<HTMLElement | null>, percent: number) {
    const last = useRef(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const to = Math.max(0, Math.min(100, percent));
        if (prefersReducedMotion()) {
            el.style.width = `${to}%`;
            last.current = to;
            return;
        }

        const anim = animate(el, {
            width: [`${last.current}%`, `${to}%`],
            duration: 900,
            ease: 'outCubic',
        });
        last.current = to;

        return () => {
            anim.pause();
        };
    }, [ref, percent]);
}

/** Entrada suave de imagem/painel (opacidade + leve zoom). */
export function fadeZoomIn(el: HTMLElement | null) {
    if (!el || prefersReducedMotion()) return;
    animate(el, { opacity: [0, 1], scale: [0.96, 1], duration: 380, ease: 'outQuad' });
}
